import { Link } from "react-router-dom";
import { Mail, ArrowLeft, Check } from "lucide-react";
import { useState } from "react";
import { supabase } from "@/lib/supabase";

export function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [sent, setSent] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setIsLoading(true);

    const { error } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: `${window.location.origin}/reset-password`,
    });

    if (error) {
      setError(error.message);
    } else {
      setSent(true);
    }
    setIsLoading(false);
  };

  if (sent) {
    return (
      <div className="min-h-screen bg-[#02182B] flex items-center justify-center p-4">
        <div className="w-full max-w-md">
          <div className="text-center mb-8">
            <h1 className="text-[#FD802E] font-bold text-3xl mb-2">Searchline Engine</h1>
          </div>

          <div className="bg-[#233D4C] rounded-lg border border-[#2a4a5c] p-8 text-center">
            <div className="w-16 h-16 bg-green-500/10 rounded-full flex items-center justify-center mx-auto mb-4">
              <Check className="text-green-400" size={32} />
            </div>

            <h2 className="text-white text-2xl font-bold mb-3">Check your email</h2>
            <p className="text-gray-400 mb-6">
              We've sent a password reset link to{" "}
              <span className="text-white font-medium">{email}</span>
            </p>

            <div className="bg-[#02182B] rounded-lg border border-[#2a4a5c] p-4 mb-6">
              <p className="text-gray-400 text-sm mb-2">Didn't receive the email?</p>
              <ul className="text-gray-500 text-xs space-y-1 text-left">
                <li>• Check your spam folder</li>
                <li>• Make sure the email address is correct</li>
                <li>• The link expires in 1 hour</li>
              </ul>
            </div>

            <button
              onClick={() => setSent(false)}
              className="w-full bg-[#FD802E] text-[#233D4C] py-3 rounded-lg font-semibold hover:bg-[#ff8f45] transition-colors mb-3"
            >
              Send another email
            </button>

            <Link
              to="/login"
              className="block text-center text-gray-400 hover:text-white text-sm transition-colors"
            >
              Back to login
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#02182B] flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <h1 className="text-[#FD802E] font-bold text-3xl mb-2">Searchline Engine</h1>
          <p className="text-gray-400">Reset your password</p>
        </div>

        <div className="bg-[#233D4C] rounded-lg border border-[#2a4a5c] p-8">
          <Link
            to="/login"
            className="flex items-center gap-2 text-gray-400 hover:text-white text-sm mb-6 transition-colors"
          >
            <ArrowLeft size={16} />
            Back to login
          </Link>

          <h2 className="text-white text-2xl font-bold mb-2">Forgot your password?</h2>
          <p className="text-gray-400 mb-6">
            No worries! Enter your email and we'll send you reset instructions.
          </p>

          <form onSubmit={handleSubmit} className="space-y-5">
            {/* Error */}
            {error && (
              <div className="bg-red-500/10 border border-red-500/30 rounded-lg px-4 py-3 text-red-400 text-sm">
                {error}
              </div>
            )}

            <div>
              <label htmlFor="email" className="block text-gray-300 text-sm font-medium mb-2">
                Email address
              </label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" size={20} />
                <input
                  type="email"
                  id="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@company.com"
                  required
                  className="w-full bg-[#02182B] border border-[#2a4a5c] rounded-lg pl-11 pr-4 py-3 text-white placeholder-gray-500 focus:outline-none focus:border-[#FD802E] focus:ring-2 focus:ring-[#FD802E]/20 transition-colors"
                />
              </div>
            </div>

            <button
              type="submit"
              disabled={isLoading}
              className="w-full bg-[#FD802E] text-[#233D4C] py-3 rounded-lg font-semibold hover:bg-[#ff8f45] transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
            >
              {isLoading ? "Sending…" : "Send reset instructions"}
            </button>
          </form>

          <div className="mt-6 p-4 bg-[#02182B] rounded-lg border border-[#2a4a5c]">
            <p className="text-gray-400 text-sm">
              <span className="text-[#FD802E] font-medium">Need help?</span> Contact our support team at{" "}
              <a href="mailto:support@searchline.com" className="text-white hover:text-[#FD802E] transition-colors">
                support@searchline.com
              </a>
            </p>
          </div>
        </div>

        <p className="text-center text-gray-400 text-sm mt-6">
          Don't have an account?{" "}
          <Link to="/signup" className="text-[#FD802E] hover:text-[#ff8f45] font-medium transition-colors">
            Sign up
          </Link>
        </p>
      </div>
    </div>
  );
}
